import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MousePointer2,
  Pen,
  Circle,
  MoveUpRight,
  Eraser,
  Undo2,
  Trash2,
  Download,
  Plus,
  Loader2,
  Check,
  PencilLine,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Sel } from "@/components/common/Sel";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { fmtMeetingDate } from "@/components/meeting/meetUtils";

const W = 1600;
const H = 900;
const COLORS = ["#E2B34B", "#F4F1EA", "#4FC3A1", "#F07C6C", "#6BB3F0", "#B58CF0"];
const WIDTHS = [2, 4, 8];
const EMPTY = { strokes: [], nodes: [], edges: [] };
const rid = () => Math.random().toString(36).slice(2, 10);

const TOOLS = [
  { key: "select", icon: MousePointer2, label: "Pilih & geser (V)" },
  { key: "pen", icon: Pen, label: "Pen (P)" },
  { key: "circle", icon: Circle, label: "Bulatan (O) — klik & tarik" },
  { key: "arrow", icon: MoveUpRight, label: "Panah relasi (A) — tarik dari bulatan ke bulatan" },
  { key: "eraser", icon: Eraser, label: "Hapus elemen (E)" },
];

function edgePoints(a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const d = Math.hypot(dx, dy) || 1;
  const ux = dx / d;
  const uy = dy / d;
  return { x1: a.x + ux * a.r, y1: a.y + uy * a.r, x2: b.x - ux * (b.r + 6), y2: b.y - uy * (b.r + 6) };
}

function strokePath(pts) {
  if (!pts.length) return "";
  if (pts.length === 1) return `M ${pts[0][0]} ${pts[0][1]} l 0.1 0`;
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const mx = (pts[i][0] + pts[i + 1][0]) / 2;
    const my = (pts[i][1] + pts[i + 1][1]) / 2;
    d += ` Q ${pts[i][0]} ${pts[i][1]} ${mx} ${my}`;
  }
  const l = pts[pts.length - 1];
  return `${d} L ${l[0]} ${l[1]}`;
}

export function WhiteboardPanel({ type }) {
  const [boards, setBoards] = useState(null);
  const [boardId, setBoardId] = useState(null);
  const [board, setBoard] = useState(null);
  const [el, setEl] = useState(EMPTY);
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState(COLORS[0]);
  const [width, setWidth] = useState(4);
  const [selected, setSelected] = useState(null); // {kind, id}
  const [draft, setDraft] = useState(null); // in-progress stroke/circle/arrow
  const [saveState, setSaveState] = useState("saved");
  const [title, setTitle] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);
  const history = useRef([]);
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const saveTimer = useRef(null);
  const labelRef = useRef(null);

  const loadBoards = useCallback(async () => {
    try {
      const { data } = await api.get("/meeting/boards", { params: { type } });
      setBoards(data);
      return data;
    } catch (e) {
      toast.error(errMsg(e, "Gagal memuat papan"));
      setBoards([]);
      return [];
    }
  }, [type]);

  useEffect(() => {
    setBoardId(null);
    setBoard(null);
    loadBoards().then((list) => {
      if (list.length) setBoardId(list[0].id);
    });
  }, [type, loadBoards]);

  useEffect(() => {
    if (!boardId) return;
    api
      .get(`/meeting/boards/${boardId}`)
      .then(({ data }) => {
        setBoard(data);
        setTitle(data.title);
        setEl({ ...EMPTY, ...(data.elements || {}) });
        history.current = [];
        setSelected(null);
        setSaveState("saved");
      })
      .catch((e) => toast.error(errMsg(e)));
  }, [boardId]);

  const persist = useCallback(
    (next) => {
      if (!boardId) return;
      setSaveState("dirty");
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(async () => {
        setSaveState("saving");
        try {
          await api.put(`/meeting/boards/${boardId}`, { elements: next });
          setSaveState("saved");
        } catch (e) {
          setSaveState("error");
          toast.error(errMsg(e, "Gagal menyimpan papan"));
        }
      }, 900);
    },
    [boardId]
  );

  const commit = useCallback(
    (next) => {
      history.current = [...history.current.slice(-49), el];
      setEl(next);
      persist(next);
    },
    [el, persist]
  );

  const undo = useCallback(() => {
    const prev = history.current.pop();
    if (!prev) return;
    setEl(prev);
    persist(prev);
    setSelected(null);
  }, [persist]);

  const removeSelected = useCallback(
    (sel = selected) => {
      if (!sel) return;
      let next = el;
      if (sel.kind === "node")
        next = { ...el, nodes: el.nodes.filter((n) => n.id !== sel.id), edges: el.edges.filter((e) => e.from !== sel.id && e.to !== sel.id) };
      if (sel.kind === "edge") next = { ...el, edges: el.edges.filter((e) => e.id !== sel.id) };
      if (sel.kind === "stroke") next = { ...el, strokes: el.strokes.filter((s) => s.id !== sel.id) };
      commit(next);
      setSelected(null);
    },
    [el, selected, commit]
  );

  // keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        undo();
        return;
      }
      if ((e.key === "Delete" || e.key === "Backspace") && selected) {
        e.preventDefault();
        removeSelected();
        return;
      }
      const map = { v: "select", p: "pen", o: "circle", a: "arrow", e: "eraser" };
      if (map[e.key.toLowerCase()] && !e.ctrlKey && !e.metaKey) setTool(map[e.key.toLowerCase()]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, removeSelected, selected]);

  const nodeMap = useMemo(() => Object.fromEntries(el.nodes.map((n) => [n.id, n])), [el.nodes]);

  const pt = (evt) => {
    const svg = svgRef.current;
    const p = svg.createSVGPoint();
    p.x = evt.clientX;
    p.y = evt.clientY;
    const r = p.matrixTransform(svg.getScreenCTM().inverse());
    return [Math.round(Math.max(0, Math.min(W, r.x))), Math.round(Math.max(0, Math.min(H, r.y)))];
  };
  const hit = (evt) => {
    const t = evt.target.closest?.("[data-kind]");
    return t ? { kind: t.getAttribute("data-kind"), id: t.getAttribute("data-id") } : null;
  };
  const nodeAt = (x, y) => [...el.nodes].reverse().find((n) => Math.hypot(n.x - x, n.y - y) <= n.r + 4);

  const onDown = (evt) => {
    if (!board) return;
    evt.currentTarget.setPointerCapture?.(evt.pointerId);
    const [x, y] = pt(evt);
    const h = hit(evt);
    if (tool === "eraser") {
      if (h) removeSelected(h);
      dragRef.current = { erasing: true };
      return;
    }
    if (tool === "select") {
      setSelected(h);
      if (h?.kind === "node") {
        const n = nodeMap[h.id];
        dragRef.current = { id: n.id, dx: x - n.x, dy: y - n.y, start: el, moved: false };
      }
      return;
    }
    if (tool === "pen") {
      setDraft({ kind: "stroke", id: rid(), color, width, points: [[x, y]] });
      return;
    }
    if (tool === "circle") {
      setDraft({ kind: "node", id: rid(), x, y, r: 30, color, label: "" });
      return;
    }
    if (tool === "arrow") {
      const n = nodeAt(x, y);
      if (!n) {
        toast.info("Mulai tarik panah dari dalam sebuah bulatan.");
        return;
      }
      setDraft({ kind: "edge", from: n.id, x, y, color });
    }
  };

  const onMove = (evt) => {
    if (!board) return;
    const [x, y] = pt(evt);
    if (dragRef.current?.erasing) {
      const h = hit(evt);
      if (h) removeSelected(h);
      return;
    }
    if (dragRef.current?.id) {
      const d = dragRef.current;
      d.moved = true;
      setEl((cur) => ({ ...cur, nodes: cur.nodes.map((n) => (n.id === d.id ? { ...n, x: x - d.dx, y: y - d.dy } : n)) }));
      return;
    }
    if (!draft) return;
    if (draft.kind === "stroke") {
      const last = draft.points[draft.points.length - 1];
      if (Math.hypot(last[0] - x, last[1] - y) > 2) setDraft({ ...draft, points: [...draft.points, [x, y]] });
    } else if (draft.kind === "node") {
      setDraft({ ...draft, r: Math.max(30, Math.round(Math.hypot(x - draft.x, y - draft.y))) });
    } else if (draft.kind === "edge") {
      setDraft({ ...draft, x, y });
    }
  };

  const onUp = (evt) => {
    if (dragRef.current?.id) {
      const d = dragRef.current;
      dragRef.current = null;
      if (d.moved) {
        history.current = [...history.current.slice(-49), d.start];
        setEl((cur) => {
          persist(cur);
          return cur;
        });
      }
      return;
    }
    dragRef.current = null;
    if (!draft) return;
    const [x, y] = pt(evt);
    if (draft.kind === "stroke") {
      const { kind, ...s } = draft;
      commit({ ...el, strokes: [...el.strokes, s] });
    } else if (draft.kind === "node") {
      const { kind, ...n } = draft;
      commit({ ...el, nodes: [...el.nodes, n] });
      setSelected({ kind: "node", id: n.id });
      setTimeout(() => labelRef.current?.focus(), 50);
    } else if (draft.kind === "edge") {
      const target = nodeAt(x, y);
      if (target && target.id !== draft.from) {
        const dup = el.edges.find((e) => e.from === draft.from && e.to === target.id);
        if (!dup) {
          const edge = { id: rid(), from: draft.from, to: target.id, color: draft.color, label: "" };
          commit({ ...el, edges: [...el.edges, edge] });
          setSelected({ kind: "edge", id: edge.id });
          setTimeout(() => labelRef.current?.focus(), 50);
        }
      }
    }
    setDraft(null);
  };

  const selNode = selected?.kind === "node" ? nodeMap[selected.id] : null;
  const selEdge = selected?.kind === "edge" ? el.edges.find((e) => e.id === selected.id) : null;
  const setLabel = (v) => {
    if (selNode) setEl((cur) => ({ ...cur, nodes: cur.nodes.map((n) => (n.id === selNode.id ? { ...n, label: v } : n)) }));
    if (selEdge) setEl((cur) => ({ ...cur, edges: cur.edges.map((e) => (e.id === selEdge.id ? { ...e, label: v } : e)) }));
  };
  const commitLabel = () => persist(el);
  const recolor = (c) => {
    setColor(c);
    if (selNode) commit({ ...el, nodes: el.nodes.map((n) => (n.id === selNode.id ? { ...n, color: c } : n)) });
    if (selEdge) commit({ ...el, edges: el.edges.map((e) => (e.id === selEdge.id ? { ...e, color: c } : e)) });
  };

  const newBoard = async () => {
    try {
      const { data } = await api.post("/meeting/boards", { type, title: "" });
      await loadBoards();
      setBoardId(data.id);
      toast.success("Papan baru dibuat");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const saveTitle = async () => {
    if (!board || title === board.title) return;
    try {
      await api.put(`/meeting/boards/${board.id}`, { title });
      setBoard({ ...board, title });
      loadBoards();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const deleteBoard = async () => {
    setConfirmDel(false);
    try {
      await api.delete(`/meeting/boards/${board.id}`);
      const list = await loadBoards();
      setBoard(null);
      setBoardId(list[0]?.id || null);
      toast.success("Papan dihapus");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const clearAll = () => commit(EMPTY);

  const exportPng = () => {
    const svg = svgRef.current;
    const clone = svg.cloneNode(true);
    clone.setAttribute("width", W);
    clone.setAttribute("height", H);
    const xml = new XMLSerializer().serializeToString(clone);
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = W;
      c.height = H;
      const ctx = c.getContext("2d");
      ctx.drawImage(img, 0, 0);
      c.toBlob((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = `${(board?.title || "papan").replace(/[^\w-]+/g, "_")}.png`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 3000);
      });
    };
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;
  };

  if (boards === null) return <div className="h-[520px] rounded-xl border border-border bg-card animate-pulse" />;

  if (!boards.length) {
    return (
      <div className="rounded-xl border border-dashed border-[hsl(220_10%_24%)] bg-card p-10 flex flex-col items-center text-center" data-testid="wb-empty">
        <PencilLine className="h-8 w-8 text-primary" />
        <h3 className="font-display text-2xl mt-3">Belum ada papan tulis</h3>
        <p className="text-sm text-muted-foreground mt-2 max-w-md">Buat papan untuk mencoret ide, menggambar bulatan dan menghubungkannya dengan panah relasi.</p>
        <Button className="mt-5 press" onClick={newBoard} data-testid="wb-new-empty"><Plus className="h-4 w-4 mr-2" /> Buat papan tulis</Button>
      </div>
    );
  }

  const cursor = tool === "pen" ? "crosshair" : tool === "circle" ? "cell" : tool === "arrow" ? "alias" : tool === "eraser" ? "not-allowed" : "default";

  return (
    <div className="space-y-3" data-testid="wb-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-[260px]">
          <Sel
            value={boardId || ""}
            onChange={setBoardId}
            options={boards.map((b) => ({ value: b.id, label: b.title }))}
            testid="wb-board-select"
          />
        </div>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} onBlur={saveTitle} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="h-10 w-[260px] bg-[hsl(220_12%_8%)]" data-testid="wb-title-input" aria-label="Judul papan" />
        {board?.date && <span className="text-xs text-muted-foreground">{fmtMeetingDate(board.date)}</span>}
        <span className="text-[11px] text-muted-foreground ml-auto flex items-center gap-1" data-testid="wb-save-state">
          {saveState === "saving" && <Loader2 className="h-3 w-3 animate-spin" />}
          {saveState === "saved" && <Check className="h-3 w-3 text-[hsl(160_55%_56%)]" />}
          {{ saved: "Tersimpan", saving: "Menyimpan…", dirty: "Belum tersimpan", error: "Gagal simpan" }[saveState]}
        </span>
        <Button variant="outline" size="sm" onClick={newBoard} data-testid="wb-new"><Plus className="h-3.5 w-3.5 mr-1.5" /> Papan baru</Button>
        <Button variant="ghost" size="sm" onClick={() => setConfirmDel(true)} className="text-muted-foreground hover:text-[hsl(0_72%_66%)]" data-testid="wb-delete-board"><Trash2 className="h-3.5 w-3.5" /></Button>
      </div>

      <div className="grid lg:grid-cols-[56px_1fr] gap-3">
        <div className="flex lg:flex-col gap-1.5 rounded-xl border border-border bg-card p-1.5 h-fit" data-testid="wb-toolbar">
          {TOOLS.map((t) => (
            <Tooltip key={t.key}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setTool(t.key)}
                  aria-pressed={tool === t.key}
                  aria-label={t.label}
                  className={`h-11 w-11 rounded-lg flex items-center justify-center transition-colors ${tool === t.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-[hsl(220_10%_13%)]"}`}
                  data-testid={`wb-tool-${t.key}`}
                >
                  <t.icon className="h-[18px] w-[18px]" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">{t.label}</TooltipContent>
            </Tooltip>
          ))}
          <div className="h-px lg:w-full w-px bg-border my-1" />
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" onClick={undo} aria-label="Undo" className="h-11 w-11 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-[hsl(220_10%_13%)]" data-testid="wb-undo">
                <Undo2 className="h-[18px] w-[18px]" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">Undo (Ctrl+Z)</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" onClick={exportPng} aria-label="Unduh PNG" className="h-11 w-11 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-[hsl(220_10%_13%)]" data-testid="wb-export">
                <Download className="h-[18px] w-[18px]" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">Unduh PNG</TooltipContent>
          </Tooltip>
        </div>

        <div className="space-y-2 min-w-0">
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card px-3 py-2">
            <div className="flex items-center gap-1.5" role="radiogroup" aria-label="Warna">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => recolor(c)}
                  aria-label={`Warna ${c}`}
                  aria-checked={color === c}
                  role="radio"
                  className={`h-7 w-7 rounded-full border-2 transition-transform ${color === c ? "border-foreground scale-110" : "border-transparent hover:scale-105"}`}
                  style={{ background: c }}
                  data-testid={`wb-color-${c.slice(1)}`}
                />
              ))}
            </div>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-1" role="radiogroup" aria-label="Ketebalan pen">
              {WIDTHS.map((w) => (
                <button
                  key={w}
                  type="button"
                  onClick={() => setWidth(w)}
                  role="radio"
                  aria-checked={width === w}
                  className={`h-8 w-10 rounded-md flex items-center justify-center ${width === w ? "bg-[hsl(43_30%_12%)] border border-[color:var(--hairline)]" : "hover:bg-[hsl(220_10%_13%)]"}`}
                  data-testid={`wb-width-${w}`}
                >
                  <span className="rounded-full bg-foreground" style={{ width: 22, height: w }} />
                </button>
              ))}
            </div>
            <div className="h-6 w-px bg-border" />
            {selNode || selEdge ? (
              <div className="flex items-center gap-2 flex-1 min-w-[240px]">
                <span className="text-[11px] text-muted-foreground whitespace-nowrap">{selNode ? "Label bulatan" : "Label panah"}</span>
                <Input
                  ref={labelRef}
                  value={(selNode || selEdge).label || ""}
                  onChange={(e) => setLabel(e.target.value)}
                  onBlur={commitLabel}
                  onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
                  placeholder={selNode ? "mis. Tim Sales" : "mis. melapor ke"}
                  className="h-8 bg-[hsl(220_12%_8%)]"
                  maxLength={60}
                  data-testid="wb-label-input"
                />
                <Button size="sm" variant="ghost" onClick={() => removeSelected()} className="text-muted-foreground hover:text-[hsl(0_72%_66%)]" data-testid="wb-delete-selected">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <span className="text-[11px] text-muted-foreground flex-1">
                {tool === "arrow" ? "Tarik dari satu bulatan ke bulatan lain untuk membuat panah relasi." : tool === "circle" ? "Klik lalu tarik untuk mengatur ukuran bulatan." : tool === "select" ? "Klik elemen untuk memilih, tarik bulatan untuk memindah. Delete untuk hapus." : tool === "eraser" ? "Klik/tarik di atas elemen untuk menghapus." : "Tulis bebas di papan."}
              </span>
            )}
            <Button size="sm" variant="ghost" onClick={clearAll} className="text-muted-foreground" data-testid="wb-clear">Bersihkan</Button>
          </div>

          <div
            className="rounded-xl border border-[color:var(--hairline)] overflow-hidden bg-[#0E1014] elev-1 mx-auto"
            style={{ maxWidth: "max(640px, calc((100vh - 230px) * 16 / 9))" }}
          >
            <svg
              ref={svgRef}
              viewBox={`0 0 ${W} ${H}`}
              xmlns="http://www.w3.org/2000/svg"
              className="w-full h-auto block select-none"
              style={{ touchAction: "none", cursor }}
              onPointerDown={onDown}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerLeave={(e) => (draft || dragRef.current) && onUp(e)}
              data-testid="wb-canvas"
            >
              <defs>
                <pattern id={`dots-${type}`} width="32" height="32" patternUnits="userSpaceOnUse">
                  <circle cx="1.5" cy="1.5" r="1.5" fill="#2A2D35" />
                </pattern>
                {COLORS.map((c) => (
                  <marker key={c} id={`arrow-${type}-${c.slice(1)}`} viewBox="0 0 10 10" refX="8" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill={c} />
                  </marker>
                ))}
              </defs>
              <rect width={W} height={H} fill="#0E1014" />
              <rect width={W} height={H} fill={`url(#dots-${type})`} />

              {el.strokes.map((s) => (
                <path
                  key={s.id}
                  d={strokePath(s.points)}
                  fill="none"
                  stroke={s.color}
                  strokeWidth={s.width * (selected?.id === s.id ? 1.6 : 1)}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity={selected?.id === s.id ? 0.8 : 1}
                  data-kind="stroke"
                  data-id={s.id}
                />
              ))}

              {el.edges.map((e) => {
                const a = nodeMap[e.from];
                const b = nodeMap[e.to];
                if (!a || !b) return null;
                const p = edgePoints(a, b);
                const sel = selected?.id === e.id;
                return (
                  <g key={e.id} data-kind="edge" data-id={e.id}>
                    <line x1={p.x1} y1={p.y1} x2={p.x2} y2={p.y2} stroke="transparent" strokeWidth="18" />
                    <line x1={p.x1} y1={p.y1} x2={p.x2} y2={p.y2} stroke={e.color} strokeWidth={sel ? 4.5 : 3} markerEnd={`url(#arrow-${type}-${(e.color || COLORS[0]).slice(1)})`} strokeDasharray={sel ? "10 6" : undefined} />
                    {e.label && (
                      <text x={(p.x1 + p.x2) / 2} y={(p.y1 + p.y2) / 2 - 10} fill="#C9C4B8" fontSize="20" textAnchor="middle" fontFamily="Figtree, sans-serif" style={{ paintOrder: "stroke", stroke: "#0E1014", strokeWidth: 6 }}>
                        {e.label}
                      </text>
                    )}
                  </g>
                );
              })}

              {draft?.kind === "edge" && nodeMap[draft.from] && (
                <line x1={nodeMap[draft.from].x} y1={nodeMap[draft.from].y} x2={draft.x} y2={draft.y} stroke={draft.color} strokeWidth="3" strokeDasharray="8 6" markerEnd={`url(#arrow-${type}-${draft.color.slice(1)})`} />
              )}

              {[...el.nodes, ...(draft?.kind === "node" ? [draft] : [])].map((n) => {
                const sel = selected?.id === n.id;
                const fs = Math.max(16, Math.min(30, n.r / 2.6));
                return (
                  <g key={n.id} data-kind="node" data-id={n.id} style={{ cursor: tool === "select" ? "grab" : undefined }}>
                    <circle cx={n.x} cy={n.y} r={n.r} fill={`${n.color}22`} stroke={n.color} strokeWidth={sel ? 4 : 2.5} />
                    {sel && <circle cx={n.x} cy={n.y} r={n.r + 8} fill="none" stroke={n.color} strokeOpacity="0.45" strokeDasharray="6 6" />}
                    {n.label && (
                      <text x={n.x} y={n.y} fill="#F4F1EA" fontSize={fs} textAnchor="middle" dominantBaseline="central" fontFamily="Figtree, sans-serif" fontWeight="600" pointerEvents="none">
                        {n.label.length > Math.floor(n.r / 7) ? `${n.label.slice(0, Math.max(4, Math.floor(n.r / 7)))}…` : n.label}
                      </text>
                    )}
                  </g>
                );
              })}

              {draft?.kind === "stroke" && (
                <path d={strokePath(draft.points)} fill="none" stroke={draft.color} strokeWidth={draft.width} strokeLinecap="round" strokeLinejoin="round" />
              )}
            </svg>
          </div>
          <p className="text-[11px] text-muted-foreground">
            {el.nodes.length} bulatan · {el.edges.length} panah · {el.strokes.length} coretan — otomatis tersimpan.
          </p>
        </div>
      </div>

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus papan "{board?.title}"?</AlertDialogTitle>
            <AlertDialogDescription>Semua coretan, bulatan dan panah di papan ini akan hilang.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction onClick={deleteBoard} data-testid="wb-delete-confirm">Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
